import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SidebarLayout from "../components/layout/SidebarLayout.jsx";
import { Button } from "../components/ui/button.jsx";
import DialerPanel from "../components/voice/DialerPanel.jsx";
import CallStage from "../components/voice/CallStage.jsx";
import CallDetailsPanel from "../components/voice/CallDetailsPanel.jsx";
import SchedulePanel from "../components/voice/SchedulePanel.jsx";
import CallActionGrid from "../components/voice/CallActionGrid.jsx";
import VoiceParticipantsSheet from "../components/voice/VoiceParticipantsSheet.jsx";
import VoiceDialerDialog from "../components/voice/VoiceDialerDialog.jsx";
import VoiceLinkDialog from "../components/voice/VoiceLinkDialog.jsx";
import VoiceInviteDialog from "../components/voice/VoiceInviteDialog.jsx";
import { Brain, Link2, Mic, Phone, Sparkles, Users } from "lucide-react";
import "../global.css"

function JourneyCard({ icon, title, steps }) {
  return (
    <div className="glass-card rounded-3xl p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-primary">{icon}</span>
        {title}
      </div>
      <ul className="mt-3 space-y-2 text-xs leading-relaxed text-muted-foreground">
        {steps.map((step, index) => (
          <li key={`${title}-${index}`} className="flex gap-2">
            <span className="mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">{index + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const generateLink = (type) => {
  const slug = Math.random().toString(36).slice(2, 10);
  return `https://call.neoin.app/${type}/${slug}`;
};

export default function VoiceCall() {
  const [callType, setCallType] = useState("video");
  const [callLink, setCallLink] = useState(() => generateLink("video"));
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [recentDial, setRecentDial] = useState(null);
  const [upcomingMeeting, setUpcomingMeeting] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [dialHistory, setDialHistory] = useState([]);
  const dialerRef = useRef(null);
  const scheduleRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    async function loadHistory() {
      try {
        const [callsRes, dialRes] = await Promise.all([
          fetch("/api/v1/voice/calls").then((res) => (res.ok ? res.json() : [])),
          fetch("/api/v1/voice/dial/history").then((res) => (res.ok ? res.json() : [])),
        ]);
        if (cancelled) return;
        const callItems = Array.isArray(callsRes)
          ? callsRes
              .filter((record) => (record?.mode || "voice") === "voice")
              .sort((a, b) => ((b.endedAt || b.createdAt || 0) - (a.endedAt || a.createdAt || 0)))
              .slice(0, 12)
              .map((record) => {
                const timestamp = record.endedAt || record.createdAt || Date.now();
                const typeLabel = record.type === "p2p" ? "1:1 call" : "Group call";
                const statusLabel = record.status === "ended" ? "Ended" : "Created";
                return {
                  id: `history-${record.id}`,
                  title: record.title || typeLabel,
                  detail: `${statusLabel} • ${new Date(timestamp).toLocaleString()}`,
                  at: timestamp,
                };
              })
          : [];
        const dialItems = Array.isArray(dialRes)
          ? dialRes
              .sort((a, b) => ((b.ts || 0) - (a.ts || 0)))
              .map((entry) => ({
                id: entry.id,
                phone: entry.phone,
                callerId: entry.callerId || "Outbound call",
                notes: entry.notes || null,
                ts: entry.ts || null,
              }))
          : [];
        setHistoryEntries(callItems);
        setDialHistory(dialItems);
        setRecentDial((prev) => prev || dialItems[0] || prev);
      } catch {
        /* ignore network errors */
      }
    }
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  const directory = useMemo(() => [
    { id: "dir-durga", name: "Durga Mohanty", email: "durga@example.com", phone: "+14155551234" },
    { id: "dir-zeina", name: "Zeina Sleiman", email: "zeina@example.com", phone: "+447123456789" },
    { id: "dir-golu", name: "Golu Baby USA", email: "golu@example.com", phone: "+14085559876" },
    { id: "dir-jiten", name: "Jiten Arora", email: "jiten@example.com", phone: "+918067890321" },
    { id: "dir-saanvi", name: "Saanvi Rao", email: "saanvi@example.com", phone: "+6591234567" },
    { id: "dir-kenji", name: "Kenji Tanaka", email: "kenji@example.com", phone: "+811234567890" },
  ], []);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [linkDialogMode, setLinkDialogMode] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [activeParticipants, setActiveParticipants] = useState(() => [
    { id: "host-jyotirmaya", name: "Jyotirmaya Mohanty", email: "jyotirmaya@neoin.app", role: "Host" },
  ]);
  const [activityLog, setActivityLog] = useState([]);

  const appendActivity = (title, detail) => {
    setActivityLog((prev) => {
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title,
        detail,
        at: Date.now(),
      };
      const next = [entry, ...prev];
      return next.slice(0, 10);
    });
  };

  const linkDialogOpen = Boolean(linkDialogMode);

  const ensureParticipant = (person, role = "Invite sent") => {
    if (!person) return;
    setActiveParticipants((prev) => {
      const identifier = (person.id || person.email || person.name || "").toLowerCase();
      if (!identifier) return prev;
      const exists = prev.some((entry) => (entry.id || entry.email || entry.name || "").toLowerCase() === identifier);
      if (exists) {
        return prev.map((entry) => {
          const entryIdentifier = (entry.id || entry.email || entry.name || "").toLowerCase();
          if (entryIdentifier === identifier && entry.role !== "Host" && role && entry.role !== role) {
            return { ...entry, role };
          }
          return entry;
        });
      }
      return [...prev, { ...person, role }];
    });
  };

  const handleParticipantInvite = (person) => {
    if (!person) return;
    const name = person.name || person.email || "Guest";
    const normalized = {
      id: person.id || person.email || name,
      name,
      email: person.email,
      phone: person.phone,
      role: person.role || "Invite sent",
    };
    ensureParticipant(normalized, normalized.role);
    appendActivity("Participant invited", `Invitation sent to ${name}`);
    toast.success(`Invitation sent to ${name}`);
  };

  const connectors = useMemo(() => [
    { name: "Zoom", description: "Schedule, launch, and sync recordings automatically.", href: "/connect" },
    { name: "WhatsApp", description: "Escalate chats into rich audio without leaving the thread.", href: "/connect" },
    { name: "LINE", description: "Engage APAC teams with localized call bridges.", href: "/connect" },
    { name: "Microsoft Teams", description: "Mirror presence and meetings into Neoin voice rooms.", href: "/connect" },
  ], []);

  const workflows = useMemo(() => [
    {
      icon: <Phone className="h-4 w-4" />,
      title: "1:1 Assist",
      blurb: "Start an instant call with AI-generated context, transcripts, and follow-up tasks.",
    },
    {
      icon: <Users className="h-4 w-4" />,
      title: "Group Rooms",
      blurb: "Spin up multi-speaker rooms with screen share, moderation, and breakout handoff.",
    },
    {
      icon: <Brain className="h-4 w-4" />,
      title: "AI Co-pilot",
      blurb: "Live sentiment, action items, and auto-summaries pumped into your CRM or docs.",
    },
    {
      icon: <Mic className="h-4 w-4" />,
      title: "Voice Apps",
      blurb: "Deploy custom IVR flows, warm transfers, and compliance recording in minutes.",
    },
  ], []);

  const journeys = useMemo(() => [
    {
      icon: <Phone className="h-4 w-4" />,
      title: "WhatsApp-style quick connect",
      steps: [
        "Tap the chat header call button to surface voice and video toggles.",
        "We preload socket context, last messages, and AI notes for you.",
        "If the participant is offline we auto-fallback to the mobile dialer.",
      ],
    },
    {
      icon: <Users className="h-4 w-4" />,
      title: "Group escalation",
      steps: [
        "Promote a thread to a room and invite via link or mobile bridging.",
        "Moderators can lock stage, drop polls, and inject AI recap live.",
        "Recordings and notes sync back into Highlights once the room closes.",
      ],
    },
    {
      icon: <Sparkles className="h-4 w-4" />,
      title: "AI co-pilot assistance",
      steps: [
        "Voice apps transcribe in real time and surface suggested actions.",
        "Auto-push follow-ups into tasks or CRM using Zapier automations.",
        "Flag sentiment changes so human agents can intervene quickly.",
      ],
    },
  ], []);

  const helperLinks = useMemo(() => [
    { label: "Connect PSTN provider", href: "/connect" },
    { label: "Configure realtime API", href: "/call" },
    { label: "Automation playbooks", href: "/ai" },
  ], []);

  const handleCallTypeChange = (type) => {
    setCallType(type);
    setCallLink(generateLink(type));
    setCallEnded(false);
    toast.success(`Switched to ${type} call`);
    appendActivity("Call type updated", `Switched to ${type} call`);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(callLink);
      toast.success("Call link copied");
    } catch {
      window.prompt("Copy call link", callLink);
    }
  };

  const handleShareLink = () => {
    setLinkDialogMode("share");
  };

  const handleSendLink = () => {
    setLinkDialogMode("send");
  };

  const handleAddPeople = (user) => {
    if (user) {
      handleParticipantInvite(user);
      return;
    }
    setInviteDialogOpen(true);
  };

  const handleSchedule = ({ title, startAt, endAt, callType: scheduledType }) => {
    const windowLabel = `${new Date(startAt).toLocaleString()} – ${new Date(endAt).toLocaleString()}`;
    setUpcomingMeeting({ title, window: windowLabel, callType: scheduledType });
    toast.success("Call scheduled and added to calendar");
    appendActivity("Call scheduled", `${title} at ${windowLabel}`);
  };

  const handleOutboundDial = (payload, contact = null) => {
    const timestamp = Date.now();
    const label = contact?.name || payload.callerId || payload.phone;
    const normalizedEntry = {
      phone: payload.phone,
      callerId: payload.callerId || label,
      notes: payload.notes || null,
      ts: timestamp,
    };

    setRecentDial((prev) => {
      const isDuplicate = prev && prev.phone === normalizedEntry.phone && prev.callerId === normalizedEntry.callerId && prev.notes === normalizedEntry.notes;
      if (!isDuplicate) {
        if (contact) {
          ensureParticipant(
            {
              id: contact.id || contact.email || contact.phone || label,
              name: contact.name || label,
              email: contact.email,
              phone: contact.phone,
              role: "Dialed",
            },
            "Dialed",
          );
        } else {
          ensureParticipant(
            {
              id: `dial-${payload.phone}`,
              name: label,
              email: null,
              phone: payload.phone,
              role: "Dialed",
            },
            "Dialed",
          );
        }
        appendActivity("Outbound dial", `Dialing ${payload.phone} as ${normalizedEntry.callerId}`);
      }
      return normalizedEntry;
    });

    setDialHistory((prev) => [normalizedEntry, ...(prev || [])].slice(0, 20));
  };

  const handleLinkDialogComplete = ({ mode, channel, contacts, manual, notes, shareTargets }) => {
    if (mode === "send") {
      const recipients = [...(contacts || []).map((entry) => entry.email || entry.name), ...(manual || [])];
      (contacts || []).forEach((contact) => {
        ensureParticipant(
          {
            id: contact.email || contact.name,
            name: contact.name || contact.email || contact.phone,
            email: contact.email,
            phone: contact.phone,
            role: "Invite sent",
          },
          "Invite sent",
        );
      });
      (manual || []).forEach((entry) => {
        ensureParticipant(
          {
            id: entry,
            name: entry,
            email: entry.includes("@") ? entry : null,
            phone: entry.includes("@") ? null : entry,
            role: "Invite sent",
          },
          "Invite sent",
        );
      });
      appendActivity("Link sent", `Delivered via ${channel}${recipients.length ? ` to ${recipients.join(", ")}` : ""}`);
      toast.success("Call link dispatched");
    } else {
      const integrations = (shareTargets || []).join(", ");
      appendActivity("Link shared", integrations ? `Shared to ${integrations}` : "Shared externally");
      toast.success("Call link shared");
    }
    if (notes) appendActivity("Invite note", notes);
    setLinkDialogMode(null);
  };

  const handleInviteDialogComplete = ({ participants = [], manual = [], message: inviteMessage, window: windowLabel }) => {
    participants.forEach((person) => handleParticipantInvite(person));
    manual.forEach((entry) => {
      ensureParticipant(
        {
          id: entry,
          name: entry,
          email: entry.includes("@") ? entry : null,
          phone: entry.includes("@") ? null : entry,
          role: "Invite sent",
        },
        "Invite sent",
      );
    });
    if (inviteMessage) appendActivity("Invite message", inviteMessage);
    if (windowLabel) appendActivity("Proposed time", windowLabel);
    if (manual.length) toast.success(`Invites queued for ${manual.length} manual contacts`);
    setInviteDialogOpen(false);
  };

  const handleStartCall = () => {
    const roomId = callLink.split("/").pop();
    setCallEnded(false);
    const endpoint = callType === "voice" ? "/api/v1/voice/calls" : "/api/v1/video/calls";
    const participantEmails = activeParticipants.map((p) => p.email).filter(Boolean);
    fetch(endpoint, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ type: participantEmails.length > 1 ? "group" : "p2p", title: `${callType === "voice" ? "Voice" : "Video"} session`, participantEmails }),
    }).catch(() => {});
    window.open(`/call?room=${encodeURIComponent(roomId)}&mode=${callType}`, "_blank", "noopener,noreferrer");
    appendActivity("Call launched", `Started ${callType} call with room ${roomId}`);
    if (callType === "voice") {
      const ts = Date.now();
      const entry = {
        id: `local-call-${ts}`,
        title: "Voice session started",
        detail: new Date(ts).toLocaleString(),
        at: ts,
      };
      setHistoryEntries((prev) => [entry, ...prev].slice(0, 20));
    }
  };

  const handleNewLink = () => {
    const next = generateLink(callType);
    setCallLink(next);
    setCallEnded(false);
    toast.success("New call link generated");
    appendActivity("Link regenerated", `Generated ${callType} link ${next}`);
  };

  const handleDialNavigate = () => {
    if (!dialerOpen) appendActivity("Dialer opened", "Reviewing contacts for outbound bridge");
    setDialerOpen(true);
    dialerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleScheduleNavigate = () => {
    scheduleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleEndCall = () => {
    toast.success("Call ended for everyone");
    appendActivity("Call ended", "Room closed for all participants");
    setCallEnded(true);
  };

  const handleOpenChat = () => {
    toast("In-call chat", { description: "Chat dock opens beside the stage" });
  };

  const handleOpenParticipants = () => {
    if (!participantsOpen) appendActivity("Participants opened", "Reviewing roster for this call");
    setParticipantsOpen(true);
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <SidebarLayout title="Voice Call Service">
        <div className="glass-card flex flex-col gap-4 rounded-3xl p-6 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-base font-semibold text-foreground">Neoin Voice unifies 1:1, group, and PSTN calls with WhatsApp-style flows.</p>
            <p className="mt-2 text-muted-foreground">Every call, message, and attachment is end-to-end encrypted and synced across your devices.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/call">
              <Button size="sm">Open demo room</Button>
            </Link>
            <Link to="/connect">
              <Button variant="secondary" size="sm" className="text-foreground">
                Configure connectors
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <CallStage
            callType={callType}
            hostName="Jyotirmaya Mohanty"
            previewImage="https://i.pravatar.cc/420?img=65"
            isMuted={isMuted}
            isCameraOff={isCameraOff}
            isScreenSharing={isScreenSharing}
            callEnded={callEnded}
            onToggleMute={() => setIsMuted((prev) => !prev)}
            onToggleCamera={() => setIsCameraOff((prev) => !prev)}
            onToggleScreen={() => setIsScreenSharing((prev) => !prev)}
            onOpenChat={handleOpenChat}
            onOpenParticipants={handleOpenParticipants}
            onEndCall={handleEndCall}
          />
          <CallDetailsPanel
            callType={callType}
            onCallTypeChange={handleCallTypeChange}
            callLink={callLink}
            onCopyLink={handleCopyLink}
            onShareLink={handleShareLink}
            onSendLink={handleSendLink}
            onAddPeople={handleAddPeople}
            recentDial={recentDial}
            upcomingMeeting={upcomingMeeting}
            directory={directory}
            activeParticipants={activeParticipants}
            activityLog={activityLog}
            historyEntries={historyEntries}
            dialHistory={dialHistory}
          />
        </div>

        <CallActionGrid
          onStart={handleStartCall}
          onNewLink={handleNewLink}
          onDial={handleDialNavigate}
          onSchedule={handleScheduleNavigate}
        />

        <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
          <div ref={scheduleRef}>
            <SchedulePanel defaultCallType={callType} onSchedule={handleSchedule} />
          </div>
          <div ref={dialerRef}>
            <DialerPanel
              contacts={directory}
              onDial={(payload) => handleOutboundDial(payload)}
            />
          </div>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {workflows.map((item) => (
            <div key={item.title} className="glass-card rounded-3xl p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-primary">{item.icon}</span>
                {item.title}
              </div>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{item.blurb}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {journeys.map((journey) => (
            <JourneyCard key={journey.title} icon={journey.icon} title={journey.title} steps={journey.steps} />
          ))}
        </div>

        <div className="mt-8 rounded-3xl border border-border bg-muted/20 p-6 text-sm">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Link2 className="h-4 w-4" /> Integrations ready out-of-the-box
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {connectors.map((connector) => (
              <div key={connector.name} className="glass-card rounded-2xl p-4 text-xs">
                <div className="font-semibold text-foreground">{connector.name}</div>
                <div className="mt-1 text-muted-foreground">{connector.description}</div>
                <Link to={connector.href} className="mt-3 inline-flex items-center gap-1 text-primary">
                  Connect now
                  <Link2 className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Need a custom bridge? Pair the Voice Call service with automation via Zapier or the realtime API to drop meetings directly into your workflows.
          </p>
          <div className="mt-4 text-xs text-muted-foreground">
            Helper links:
            <ul className="mt-2 grid gap-1 text-primary">
              {helperLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="hover:text-primary/80">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <VoiceLinkDialog
          open={linkDialogOpen}
          mode={linkDialogMode || "send"}
          onOpenChange={(value) => {
            if (!value) setLinkDialogMode(null);
          }}
          callLink={callLink}
          directory={directory}
          onComplete={handleLinkDialogComplete}
        />
        <VoiceInviteDialog
          open={inviteDialogOpen}
          onOpenChange={(value) => setInviteDialogOpen(value)}
          directory={directory}
          callLink={callLink}
          onInvite={handleInviteDialogComplete}
        />
        <VoiceDialerDialog
          open={dialerOpen}
          onOpenChange={(value) => setDialerOpen(value)}
          contacts={directory}
          onDial={handleOutboundDial}
        />
        <VoiceParticipantsSheet
          open={participantsOpen}
          onOpenChange={(value) => {
            if (value && !participantsOpen) {
              appendActivity("Participants opened", "Reviewing roster for this call");
            }
            setParticipantsOpen(value);
          }}
          participants={activeParticipants}
          directory={directory}
          onInvite={handleParticipantInvite}
        />
      </SidebarLayout>
    </section>
  );
}
